import axios from 'axios';
import { getStorage, removeStorage } from './localstorage';
import { reportGlobalError } from './globalError';

axios.interceptors.request.use(
  (config) => {
    const authToken = getStorage('token');
    // eslint-disable-next-line no-param-reassign
    config.headers.common.Authorization = `Bearer ${authToken}`;
    return config;
  },
  (error) => Promise.reject(error),
);

const RETRY_LIMIT = 2;
const RETRY_DELAY_MS = 700;
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    if (error.response?.status === 401) {
      removeStorage('token');
      removeStorage('remember');
      window.location.href = '/auth/login';
      return Promise.reject(error);
    }

    // Network hiccup ชั่วคราว (ไม่มี response กลับมาเลย) — retry เงียบ ๆ ก่อน
    // แจ้ง dialog เฉพาะ GET เพราะ retry POST/PUT/DELETE เสี่ยงยิงซ้ำถ้า request แรกสำเร็จฝั่ง server แล้วแค่ response หลุด
    const { config } = error;
    const isNetworkError = !error.response;
    const isRetriableMethod = !config?.method || config.method.toLowerCase() === 'get';
    if (config && isNetworkError && isRetriableMethod) {
      config.__retryCount = config.__retryCount || 0;
      if (config.__retryCount < RETRY_LIMIT) {
        config.__retryCount += 1;
        await wait(RETRY_DELAY_MS * config.__retryCount);
        return axios(config);
      }
    }

    // แจ้ง error ทุก response ที่ไม่สำเร็จผ่าน dialog กลาง — ไม่ต้อง try/catch ทีละหน้า
    // backend ส่ง error กลับมาเป็น { error: { status, message } } เสมอ (ดู responseFormat.js res.error) —
    // เดิม path นี้ขาด .error. ตรงกลาง เลยได้ undefined แล้ว fallback ไปใช้ error.message ของ Axios เอง
    // ("Request failed with status code 400") แทนข้อความจริงจาก backend ทุกจุดที่ error ผ่าน interceptor นี้
    const message =
      error.response?.data?.error?.message ||
      error.message ||
      'เกิดข้อผิดพลาด ไม่สามารถเชื่อมต่อเซิร์ฟเวอร์ได้';
    // eslint-disable-next-line no-param-reassign
    error.__globalErrorHandled = true;
    // request เบื้องหลัง (เช่นดึงข้อมูลสดเป็นระยะ) ส่ง { __silentError: true } มา — ล้มเหลวแล้วไม่ต้องเด้ง dialog
    // ให้ผู้ใช้ทุกรอบ (ข้อมูลเดิมยังอยู่ รอบหน้าลองใหม่เอง) ส่วน 401 ด้านบนยัง logout ตามปกติ
    if (!config?.__silentError) reportGlobalError(message, error.response?.status);
    return Promise.reject(error);
  },
);

export default axios;
